/**
 * Kayıtlar: the audit chain as a read-only feed. Rows never insert themselves (design rule
 * 10): a minute timer counts what arrived after the newest shown and a "N yeni olay" button
 * reloads. Filter tabs by kind; an approval event links to its card.
 */

import { useEffect, useState } from 'react';
import { LOG_KIND_LABELS, LOG_KINDS, LOG_RESULT_LABELS, type LogEvent, type LogKind } from '../api/log.js';
import { type LogState, NEW_EVENTS_CHECK_MS, useLog } from '../app/log.js';
import { Link } from '../app/router.js';
import { formatRelativeClock } from '../lib/format.js';
import { EmptyState, Segmented } from '../screens/common.js';

type LogFilter = 'tumu' | LogKind;

function LogStatus({ state, reload }: { state: LogState; reload: () => void }) {
  if (state.error !== null && state.view === null) {
    return (
      <div className="shop-status" role="status">
        Kayıtlar yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={reload}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className="shop-status" role="status">
      Yükleniyor…
    </div>
  );
}

function LogRow({ event, now }: { event: LogEvent; now: number }) {
  const bad = event.result !== 'ok' && event.result !== 'dry_run';
  return (
    <li className={`log-row log-row--${event.kind}${bad ? ' log-row--bad' : ''}`}>
      <span className="log-row__time mono">{formatRelativeClock(event.at, now)}</span>
      <span className={`log-row__actor log-row__actor--${event.actorType}`}>{event.actor}</span>
      <span className="log-row__title">
        {event.title}
        {event.result !== 'ok' ? <span className="log-row__result"> · {LOG_RESULT_LABELS[event.result]}</span> : null}
      </span>
      {event.detail ? <span className="log-row__detail">{event.detail}</span> : null}
      {event.approvalId ? (
        <Link to={`/onay/${event.approvalId}`} className="log-row__link">
          Onay #{event.approvalId}
        </Link>
      ) : null}
    </li>
  );
}

export function LogPage({ now }: { now: number }) {
  const { store, state } = useLog();
  const [filter, setFilter] = useState<LogFilter>('tumu');
  useEffect(() => {
    void store.ensureLoaded();
    const timer = setInterval(() => void store.checkNew(), NEW_EVENTS_CHECK_MS);
    return () => clearInterval(timer);
  }, [store]);
  if (state.view === null) return <LogStatus state={state} reload={() => void store.reload()} />;
  const view = state.view;
  if (view.events.length === 0) {
    return (
      <EmptyState
        title="Henüz kayıt yok"
        text="Sistem çalıştıkça onaylar, görevler ve bağlantı işleri burada birikir."
      />
    );
  }
  const counts = new Map<LogKind, number>();
  for (const event of view.events) counts.set(event.kind, (counts.get(event.kind) ?? 0) + 1);
  const options: { id: LogFilter; label: string; count: number | null }[] = [
    { id: 'tumu', label: 'Tümü', count: view.events.length },
    ...LOG_KINDS.filter((kind) => (counts.get(kind) ?? 0) > 0).map((kind) => ({
      id: kind as LogFilter,
      label: LOG_KIND_LABELS[kind],
      count: counts.get(kind) ?? 0,
    })),
  ];
  const shown = filter === 'tumu' ? view.events : view.events.filter((event) => event.kind === filter);
  return (
    <>
      {state.pending > 0 ? (
        <button type="button" className="log-new" onClick={() => void store.reload()}>
          {state.pending} yeni olay · yükle
        </button>
      ) : null}
      <div className="shop-filters">
        <Segmented<LogFilter>
          label="Kayıt türü"
          controls="kayit-liste"
          value={filter}
          onChange={setFilter}
          options={options}
        />
      </div>
      <div id="kayit-liste" role="tabpanel" aria-label="Kayıt listesi">
        <ol className="log-list" aria-label="Olaylar">
          {shown.map((event) => (
            <LogRow key={event.id} event={event} now={now} />
          ))}
        </ol>
        <p className="shop-hint">
          {view.hasMore ? 'Son 200 olay gösteriliyor.' : 'Kayıtların tamamı bu kadar.'} Yeni olaylar kendiliğinden
          eklenmez; “yeni olay” düğmesiyle gelir.
        </p>
      </div>
    </>
  );
}
